import { Outlet, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard, FolderOpen, Users, Mic2, Palette, Settings,
  ChevronDown, LogOut, Film
} from "lucide-react";
import { useState } from "react";

const navItems = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
];

const assetItems = [
  { to: "/agents", icon: Users, label: "Agents" },
  { to: "/voices", icon: Mic2, label: "Voices" },
  { to: "/brand-kits", icon: Palette, label: "Brand Kits" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [assetsOpen, setAssetsOpen] = useState(
    assetItems.some(i => location.pathname.startsWith(i.to))
  );

  const linkClass = (isActive) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 ${
      isActive
        ? "bg-[#4D11F6] text-white"
        : "text-[#94969C] hover:text-white hover:bg-white/8"
    }`;

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <aside className="w-64 bg-[#020B23] flex flex-col shrink-0" data-testid="sidebar">
        {/* Logo */}
        <div className="px-6 py-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-[#4D11F6] flex items-center justify-center">
              <Film className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="text-white font-semibold text-lg tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              Multiplai
            </span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) => linkClass(isActive)}
            >
              <item.icon size={18} strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}

          {/* Asset Library */}
          <button
            onClick={() => setAssetsOpen(!assetsOpen)}
            className="flex items-center justify-between w-full px-4 py-2.5 rounded-lg text-sm font-medium text-[#94969C] hover:text-white hover:bg-white/8 transition-colors duration-150"
            data-testid="nav-asset-library"
          >
            <span className="flex items-center gap-3">
              <FolderOpen size={18} strokeWidth={1.5} />
              Asset Library
            </span>
            <ChevronDown
              size={14}
              className={`transition-transform duration-200 ${assetsOpen ? "rotate-180" : ""}`}
            />
          </button>
          {assetsOpen && (
            <div className="ml-4 space-y-0.5 animate-fade-in">
              {assetItems.map(item => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={({ isActive }) => linkClass(isActive)}
                >
                  <item.icon size={16} strokeWidth={1.5} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}

          {/* Integrations */}
          <NavLink
            to="/integrations"
            data-testid="nav-integrations"
            className={({ isActive }) => linkClass(isActive)}
          >
            <Settings size={18} strokeWidth={1.5} />
            Integrations
          </NavLink>
        </nav>

        {/* User Info */}
        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#7A81E9] flex items-center justify-center text-white text-xs font-semibold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-[#676970] text-xs truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="p-1.5 text-[#676970] hover:text-white transition-colors"
              data-testid="logout-btn"
              title="Sign out"
            >
              <LogOut size={16} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-[#F3F3F9]">
        <Outlet />
      </main>
    </div>
  );
}
